import Header from "../header/header";
import Footer from "../footer/footer";
import NavBar from "../nav/nav-bar";
import ReportNav from "./report-nav";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { faCommentsDollar } from "@fortawesome/free-solid-svg-icons";
import { useEffect, useState } from "react";
import Service from "./../webservice/http";
import { useHistory } from "react-router-dom";
import { BsFillCollectionFill } from "react-icons/bs";
import { FaRupeeSign, FaFilter } from "react-icons/fa";
import { GiOnTarget } from "react-icons/gi";
import highchartsMore from "highcharts/highcharts-more.js";
import solidGauge from "highcharts/modules/solid-gauge.js";
import { storage } from "../utils/storage";
import pesoSign from "../assets/images/peso-sign-solid-full.svg";
import ReportFilter from "./report-filter";
import ReportFilterBanner from "./report-filter-banner";

const CollectionReport = () => {
  const history = useHistory();
  const services = new Service();
  highchartsMore(Highcharts);
  solidGauge(Highcharts);

  const role = storage.getItem("role");
  const isAgentRole =
    role === "Agent" ||
    role === "Agenth" ||
    role === "Agent Collections" ||
    role.includes("Agent");

  const [collectionPredictionCategories, setCollectionPredictionCategories] =
    useState([]);
  const [collectionPredictionSeries, setCollectionPredictionSeries] = useState(
    []
  );

  const [collectionData, setCollectionData] = useState([]);

  // ── Report filter state (non-agent roles) ──
  const [reportFilterWidth, setReportFilterWidth] = useState(0);
  const [isReportFilterOpen, setIsReportFilterOpen] = useState(false);
  const [reportFilters, setReportFilters] = useState({});

  const changeReportFilterWidth = () => {
    if (reportFilterWidth === 550) {
      setReportFilterWidth(0);
      setIsReportFilterOpen(false);
    } else {
      setReportFilterWidth(550);
      setIsReportFilterOpen(true);
    }
  };
  const handleReportFilterApply = (f) => setReportFilters(f);
  const handleReportFilterReset = () => setReportFilters({});

  const buildQueryParams = (filters) => {
    const params = new URLSearchParams();
    const mapping = {
      lob: "lob_name",
      manager: "manager_name",
      team_lead: "team_lead_name",
      agent: "agent_name",
      dca_organization: "dca_organization_name",
      dca_manager: "dca_manager_name",
      dca_team: "dca_team_lead_name",
      dca_agent: "dca_agent_name",
      collection_strategy: "collection_strategy",
      product_type: "product_name",
      time_period: "period",
      from_date: "from_date",
      to_date: "to_date",
      bucket_type: "bucket_type",
    };
    for (const [key, value] of Object.entries(filters)) {
      if (mapping[key] && value) params.append(mapping[key], value);
      else if (value && !mapping[key]) params.append(key, value);
    }
    return params.toString().replace(/\+/g, "%20");
  };

  async function fetchCollectionAnalysis(days) {
    try {
      const res = await services.get(
        `api/report/collection_analysis/?days=${days}`
      );
      if (!res || res === "TypeError: Failed to fetch") return;
      if (res.code === "token_not_valid") {
        storage.clear();
        history.push("/login");
        return;
      }
      setCollectionData(res);
      const items = res.collection_prediction_analysis || [];
      if (items.length === 0) return;

      setCollectionPredictionCategories(items.map((item) => item.status));
      setCollectionPredictionSeries([
        { name: "Prediction", data: items.map((item) => item.prediction) },
        { name: "Actual", data: items.map((item) => item.actual) },
      ]);
    } catch (e) {
      console.warn("Collection analysis API error:", e.message);
    }
  }

  // Hierarchy collection analysis (non-agent roles)
  async function fetchHierarchyCollectionAnalysis(filters = {}) {
    try {
      const qs = buildQueryParams(filters);
      const url = `api/call_verification/hierarchy-collection-analysis/${
        qs ? `?${qs}` : ""
      }`;
      const res = await services.get(url);
      if (!res || res === "TypeError: Failed to fetch") return;
      if (res.code === "token_not_valid") {
        storage.clear();
        history.push("/login");
        return;
      }

      const items = res.prediction_analysis || [];

      // Build chart state from hierarchy response
      setCollectionPredictionCategories(items.map((item) => item.display_name));
      setCollectionPredictionSeries([
        {
          name: "Predicted",
          data: items.map((item) => item.predicted_count ?? 0),
        },
        { name: "Actual", data: items.map((item) => item.actual_count ?? 0) },
      ]);

      // Aggregate summary stats from items
      const totalCollected = items.reduce((s, i) => s + (i.collected ?? 0), 0);
      const avgAccuracy = items.length
        ? items.reduce((s, i) => s + (i.accuracy ?? 0), 0) / items.length
        : 0;
      const avgSuccessPoint = items.length
        ? items.reduce((s, i) => s + (i.success_point ?? 0), 0) / items.length
        : 0;

      // Shape into the same structure collectionData readers expect
      setCollectionData({
        collected_amount: {
          value: totalCollected.toLocaleString("en-IN", {
            maximumFractionDigits: 2,
          }),
          formatted: `₹${totalCollected.toLocaleString("en-IN", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}`,
        },
        prediction_accuracy: { value: avgAccuracy.toFixed(1) },
        success_point: {
          value: parseFloat(avgSuccessPoint.toFixed(2)),
          formatted: `${avgSuccessPoint.toFixed(2)}%`,
        },
      });
    } catch (e) {
      console.warn("Hierarchy collection API error:", e.message);
    }
  }

  console.log(collectionData);

  useEffect(() => {
    if (isAgentRole) fetchCollectionAnalysis(7);
    else fetchHierarchyCollectionAnalysis();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Re-fetch hierarchy data when filters change
  useEffect(() => {
    if (!isAgentRole) fetchHierarchyCollectionAnalysis(reportFilters);
  }, [reportFilters]); // eslint-disable-line react-hooks/exhaustive-deps

  const collectionPredictionOption = {
    chart: {
      type: "column",
      height: (3 / 5) * 100 + "%",
    },
    title: {
      text: "",
    },
    xAxis: {
      categories: collectionPredictionCategories,
    },
    credits: {
      enabled: false,
    },
    series: collectionPredictionSeries,
  };

  /**
   * Gauge chart option
   */
  const gaugeOption = {
    chart: {
      type: "gauge",
      plotBackgroundColor: null,
      plotBackgroundImage: null,
      plotBorderWidth: 0,
      plotShadow: false,
    },

    title: {
      text: "",
    },

    pane: {
      startAngle: -150,
      endAngle: 150,
      background: [
        {
          backgroundColor: {
            linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 },
            stops: [
              [0, "#FFF"],
              [1, "#333"],
            ],
          },
          borderWidth: 0,
          outerRadius: "109%",
        },
        {
          backgroundColor: {
            linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 },
            stops: [
              [0, "#333"],
              [1, "#FFF"],
            ],
          },
          borderWidth: 1,
          outerRadius: "107%",
        },
        {
          // default background
        },
        {
          backgroundColor: "#DDD",
          borderWidth: 0,
          outerRadius: "105%",
          innerRadius: "103%",
        },
      ],
    },

    // the value axis
    yAxis: {
      min: 0,
      max: 100,
      // innerRadius: "250%",
      minorTickInterval: "auto",
      minorTickWidth: 1,
      minorTickLength: 10,
      minorTickPosition: "inside",
      minorTickColor: "#666",

      tickPixelInterval: 30,
      tickWidth: 2,
      tickPosition: "inside",
      tickLength: 10,
      tickColor: "#666",
      labels: {
        step: 2,
        rotation: "auto",
      },
      title: {
        text: "%",
      },
      plotBands: [
        {
          from: 0,
          to: 30,
          color: "#DF5353",
        },
        {
          from: 30,
          to: 45,
          color: "#DDDF0D",
        },
        {
          from: 45,
          to: 75,
          color: "#70EDA5",
        },
        {
          from: 75,
          to: 100,
          color: "#55BF3B",
        },
      ],
    },

    series: [
      {
        name: "Success Point",
        data: [collectionData?.success_point?.value ?? 0],
        tooltip: {
          valueSuffix: "%",
        },
      },
    ],
    credits: {
      enabled: false,
    },
  };

  const changeReportInterval = (e) => {
    const day = e.target.id;
    fetchCollectionAnalysis(day);
  };

  /**
   * Outlier analysis bubble chart config
   */
  const outlierOption = {
    chart: {
      type: "bubble",
      plotBorderWidth: 1,
      zoomType: "xy",
    },

    title: {
      text: "",
    },
    credits: {
      enabled: false,
    },
    xAxis: {
      gridLineWidth: 1,
      accessibility: {
        rangeDescription: "Range: 0 to 100.",
      },
      gridLineColor: "transparent",
    },

    yAxis: {
      startOnTick: false,
      endOnTick: false,
      accessibility: {
        rangeDescription: "Range: 0 to 100.",
      },
      gridLineColor: "transparent",
    },

    series: [
      {
        name: "Settlement",
        data: [
          [85, 1, 20],
          [65, 2, 45],
          [45, 3, 30],
          [25, 4, 23],
        ],
        marker: {
          fillColor: {
            radialGradient: { cx: 0.4, cy: 0.3, r: 0.7 },
            stops: [
              [0, "rgba(255,235,255,0.5)"],
              [
                1,
                Highcharts.color(Highcharts.getOptions().colors[0])
                  .setOpacity(0.5)
                  .get("rgba"),
              ],
            ],
          },
        },
      },
      {
        name: "Partial Settlement",
        data: [
          [75, 1, 90],
          [55, 2, 25],
          [35, 3, 60],
          [15, 4, 43],
        ],
        marker: {
          fillColor: {
            radialGradient: { cx: 0.4, cy: 0.3, r: 0.7 },
            stops: [
              [0, "rgba(255,5,255,0.5)"],
              [
                1,
                Highcharts.color(Highcharts.getOptions().colors[1])
                  .setOpacity(0.5)
                  .get("rgba"),
              ],
            ],
          },
        },
      },
      {
        name: "Promise Broken",
        data: [
          [89, 1, 90],
          [69, 2, 25],
          [39, 3, 60],
          [29, 4, 43],
        ],
        marker: {
          fillColor: {
            radialGradient: { cx: 0.4, cy: 0.3, r: 0.7 },
            stops: [
              [0, "rgba(25,255,255,0.5)"],
              [
                1,
                Highcharts.color(Highcharts.getOptions().colors[1])
                  .setOpacity(0.5)
                  .get("rgba"),
              ],
            ],
          },
        },
      },
      {
        name: "Denial",
        data: [
          [95, 1, 90],
          [75, 2, 25],
          [45, 3, 60],
          [25, 4, 43],
        ],
        marker: {
          fillColor: {
            radialGradient: { cx: 0.4, cy: 0.3, r: 0.7 },
            stops: [
              [0, "rgba(255,255,55,0.5)"],
              [
                1,
                Highcharts.color(Highcharts.getOptions().colors[1])
                  .setOpacity(0.5)
                  .get("rgba"),
              ],
            ],
          },
        },
      },
    ],
  };

  return (
    <div className="dashboard-body">
      <Header />
      <NavBar />

      <div>
        <div className="container-fluid">
          <div className="my-call">
            <div
              className="side-panel-with-table clearfix"
              style={{ display: "flex" }}
            >
              <ReportNav />

              <div className="call-table" style={{ padding: "2px 10px" }}>
                {/* Active filter banner */}
                {!isAgentRole && (
                  <ReportFilterBanner
                    filters={reportFilters}
                    onClear={handleReportFilterReset}
                  />
                )}
                <div className="my-calls-column" style={{ padding: "10px" }}>
                  <div className="calls-top-pannel">
                    <div className="row">
                      <div className="col-lg-6">
                        <div className="pannel-heading clearfix">
                          <div className="pannel-heading-icon mt-4">
                            <BsFillCollectionFill />
                          </div>
                          <div className="pannel-heading-info mt-2">
                            <h3>Collection </h3>
                            <p>Visualize your collection efficiency</p>
                          </div>
                        </div>
                      </div>

                      {/* 7/30/90 tabs — agent/agenth only */}
                      {isAgentRole && (
                        <div className="col-lg-6 mt-4">
                          <div className="pannel-nav clearfix">
                            <ul className="clearfix">
                              <div
                                className="btn-group btn-group-toggle"
                                data-toggle="buttons"
                              >
                                <label
                                  className="btn btn-default active"
                                  id="7"
                                  onClick={changeReportInterval}
                                >
                                  <input
                                    type="radio"
                                    name="options"
                                    autoComplete="off"
                                  />{" "}
                                  7 Days
                                </label>
                                <label
                                  className="btn btn-default"
                                  id="30"
                                  onClick={changeReportInterval}
                                >
                                  <input
                                    type="radio"
                                    name="options"
                                    autoComplete="off"
                                  />{" "}
                                  30 Days{" "}
                                </label>
                                <label
                                  className="btn btn-default"
                                  id="90"
                                  onClick={changeReportInterval}
                                >
                                  <input
                                    type="radio"
                                    name="options"
                                    autoComplete="off"
                                  />{" "}
                                  90 Days
                                </label>
                              </div>
                            </ul>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div
                  style={{
                    display: "flex",
                    gap: "10px",
                    alignItems: "stretch",
                    marginTop: "10px",
                  }}
                >
                  <div style={{ flex: "0 0 60%", maxWidth: "60%" }}>
                    <div className="all-list" style={{ height: "651px" }}>
                      <label>Collection Prediction Analysis</label>
                      <HighchartsReact
                        highcharts={Highcharts}
                        options={collectionPredictionOption}
                      />
                    </div>
                  </div>

                  <div
                    style={{
                      flex: "1",
                      display: "flex",
                      flexDirection: "column",
                      gap: "10px",
                    }}
                  >
                    {/* Stat cards row */}
                    <div style={{ display: "flex", gap: "10px" }}>
                      {/* Collected card */}
                      <div style={{ flex: 1 }}>
                        <div
                          className="all-list"
                          style={{
                            height: "170px",
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            textAlign: "center",
                          }}
                        >
                          {storage
                            .getItem("usecase")
                            .includes("Collections") ? (
                            <img
                              src={pesoSign}
                              alt=""
                              className=" w-12 h-12
                            "
                            />
                          ) : (
                            <FaRupeeSign size="50" />
                          )}
                          <h4>{collectionData?.collected_amount?.value}</h4>
                          <p>Collected</p>
                        </div>
                      </div>

                      <div style={{ flex: 1 }}>
                        <div
                          className="all-list"
                          style={{
                            height: "170px",
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            textAlign: "center",
                          }}
                        >
                          <GiOnTarget size="50" />
                          <h4>
                            {collectionData?.prediction_accuracy?.value} %
                          </h4>
                          <p>Prediction Accuracy</p>
                        </div>
                      </div>
                    </div>

                    <div style={{ flex: 1 }}>
                      <div className="all-list" style={{ height: "470px" }}>
                        <label>Success Point</label>
                        {collectionData?.success_point && (
                          <div
                            style={{
                              textAlign: "center",
                              fontSize: "13px",
                              color: "#555",
                              marginTop: "4px",
                            }}
                          >
                            <strong>
                              {collectionData.success_point.formatted}
                            </strong>
                            {/* <span style={{ marginLeft: "8px", color: "#888" }}>
                              {collectionData.success_point.description}
                            </span> */}
                          </div>
                        )}
                        <HighchartsReact
                          highcharts={Highcharts}
                          options={gaugeOption}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div style={{ marginTop: "10px" }}>
                  <div className="all-list" style={{ height: "425px" }}>
                    <label>Outlier Analysis</label>
                    <HighchartsReact
                      highcharts={Highcharts}
                      options={outlierOption}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />

      {/* Floating ReportFilter — non-agent roles only */}
      {!isAgentRole && (
        <>
          <button
            onClick={changeReportFilterWidth}
            title="Global Filters"
            style={{
              position: "fixed",
              transition: "0.5s",
              top: "30%",
              right: reportFilterWidth > 0 ? reportFilterWidth + 8 : 25,
              backgroundColor:
                Object.keys(reportFilters).length > 0 ? "#271078" : "#F99E52",
              color: "white",
              padding: "10px",
              height: "40px",
              width: "40px",
              zIndex: 2001,
              borderRadius: "10%",
              border: "none",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: "0 4px 12px rgba(0,0,0,0.25)",
            }}
          >
            <FaFilter size={18} />
            {Object.keys(reportFilters).length > 0 && (
              <span
                style={{
                  position: "absolute",
                  top: "-4px",
                  right: "-4px",
                  backgroundColor: "#F99E52",
                  color: "#fff",
                  borderRadius: "50%",
                  width: "18px",
                  height: "18px",
                  fontSize: "10px",
                  fontWeight: "bold",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {Object.keys(reportFilters).length}
              </span>
            )}
          </button>

          <ReportFilter
            width={reportFilterWidth}
            changeSideBarWidth={changeReportFilterWidth}
            onApplyFilter={handleReportFilterApply}
            onResetFilter={handleReportFilterReset}
            userRole={role}
          />

          {isReportFilterOpen && (
            <div
              onClick={changeReportFilterWidth}
              style={{
                position: "fixed",
                inset: 0,
                backgroundColor: "rgba(0,0,0,0.35)",
                zIndex: 1999,
              }}
            />
          )}
        </>
      )}
    </div>
  );
};

export default CollectionReport;
